import { useState } from 'react'
import { Send, Check, Loader2 } from 'lucide-react'
import { useAuth } from '@/contexts/auth-context'
import { useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { SUPPORT_EMAIL } from '@/lib/config/site'

export function ContactForm({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const { user } = useAuth()
  const submitAction = useAction(api.supportEmails.sendContactEmail)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    const formData = new FormData(e.target as HTMLFormElement)
    const type = formData.get('type') as string
    const emailValue = formData.get('email') as string
    const message = formData.get('message') as string

    try {
      await submitAction({
        type,
        email: emailValue || user?.email || '',
        message,
      })

      setIsSuccess(true)
      setEmail('')
      ;(e.target as HTMLFormElement).reset()

      setTimeout(() => setIsSuccess(false), 5000)
    } catch (err) {
      console.error('Error submitting support ticket:', err)
      setError(`Failed to send message. Please try again or email ${SUPPORT_EMAIL} directly.`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-card">
      <form onSubmit={handleSubmit}>
        <div className="p-6">
          {!hideHeader && (
            <>
              <h3 className="text-lg font-medium text-foreground">Contact Us</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Send us a message and we'll get back to you as soon as possible.
              </p>
            </>
          )}

          {isSuccess && (
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-center gap-2">
              <Check size={16} className="text-emerald-600 shrink-0" />
              <p className="text-sm text-emerald-700">Your message has been sent successfully.</p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="mt-5 space-y-4">
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-foreground mb-1.5">
                Type of Inquiry
              </label>
              <select
                id="type"
                name="type"
                required
                className="w-full max-w-xs px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
              >
                <option value="general">General Inquiry</option>
                <option value="refund">Refund Request</option>
                <option value="feature">Feature Idea</option>
                <option value="bug">Bug Report</option>
                <option value="other">Something Else</option>
              </select>
            </div>

            {!user && (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="your@email.com"
                  className="w-full max-w-xs px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                />
              </div>
            )}

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground mb-1.5">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={4}
                placeholder="Tell us how we can help..."
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 resize-none"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-muted/30">
          <p className="text-sm text-muted-foreground">We typically respond within 24 hours.</p>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-foreground text-background rounded-md hover:bg-foreground/90 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
            {isSubmitting ? 'Sending...' : 'Send'}
          </button>
        </div>
      </form>
    </div>
  )
}
